// src/App.tsx
import React from "react";
import { Routes, Route } from "react-router-dom";

import DemoBotPage from "./pages/DemoBotPage";
import LandingPage from "./pages/LandingPage";
import PolicyPage from "./pages/PolicyPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";

import PublicLayout from "./layouts/PublicLayout";
import AppLayout from "./layouts/AppLayout";
import RequireAuth from "./components/RequireAuth";
import RequireRole from "./components/RequireRole";
import ReferralTracker from "./components/ReferralTracker";

import BotWhatsappTemplatesPage from "./pages/app/BotWhatsappTemplatesPage";

import BotsListPage from "./pages/app/BotsListPage";
import BotCreatePage from "./pages/app/BotCreatePage";
import BotDetailPage from "./pages/app/BotDetailPage";
import BotChannelsPage from "./pages/app/BotChannelsPage";
import BotConversationsPage from "./pages/app/BotConversationsPage";
import ConversationDetailPage from "./pages/app/ConversationDetailsPage";
import BotKnowledgePage from "./pages/app/BotKnowledgePage";
import BotKnowledgeJobPage from "./pages/app/BotKnowledgeJobPage";
import BotFeaturesPage from "./pages/app/BotFeaturesPage";
import TermsPage from "./pages/TermsPage";
import BillingPage from "./pages/app/BillingPage";
import ProfilePage from "./pages/app/ProfilePage";
import DashboardPage from "./pages/app/DashboardPage";
import DashboardBotHealthPage from "./pages/app/DashboardBotHealthPage";
import DashboardConversionPage from "./pages/app/DashboardConversionPage";
import WidgetBotPage from "./pages/WidgetBotPage";

// Existing
import BotPlanPage from "./pages/app/BotPlanPage";

// NEW
import BotSettingsPage from "./pages/app/BotSettingsPage";

import ReferrerPortalPage from "./pages/app/ReferrerPortalPage";
import AdminReferralsPage from "./pages/app/admin/AdminReferralsPage";
import AdminReferralPartnerDetailPage from "./pages/app/admin/AdminReferralPartnerDetailPage";
import AdminUsersPage from "./pages/app/admin/AdminUsersPage";
import AdminBotsPage from "./pages/app/admin/AdminBotsPage";
import AdminBookingsPage from "./pages/app/admin/AdminBookingsPage";
import AdminEmailUsagePage from "./pages/app/admin/AdminEmailUsagePage";
import AdminPaymentsPage from "./pages/app/admin/AdminPaymentsPage";
import AdminOpenAIUsagePage from "./pages/app/admin/AdminOpenAIUsagePage";
import AdminIntegrationsPage from "./pages/app/admin/AdminIntegrationsPage";
import AdminPlansPage from "./pages/app/admin/AdminPlansPage";


import OnboardingCreateBotPage from "./pages/onboarding/OnboardingCreateBotPage";
import OnboardingChannelsPage from "./pages/onboarding/OnboardingChannelsPage";
import OnboardingBookingPage from "./pages/onboarding/OnboardingBookingPage";
import OnboardingLeadAdsPage from "./pages/onboarding/OnboardingLeadAdsPage";
import OnboardingPlanPage from "./pages/onboarding/OnboardingPlanPage";
import OnboardingKnowledgePage from "./pages/onboarding/OnboardingKnowledgePage";

const App: React.FC = () => {
  return (
    <div className="app-root">
      <ReferralTracker />

      <Routes>
        {/* Public layout */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/policy" element={<PolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/demo/:slug" element={<DemoBotPage />} />
        </Route>

        <Route path="/widget/:slug" element={<WidgetBotPage />} />


        {/* NEW: onboarding wizard, public layout but requires auth */}
    <Route
      path="/onboarding"
      element={
        <RequireAuth>
          <PublicLayout />
        </RequireAuth>
      }
    >
      {/* Step 1: create bot */}
      <Route path="bots/new" element={<OnboardingCreateBotPage />} />

      {/* Steps 2–5 use bot id */}
      <Route path="bots/:id/channels" element={<OnboardingChannelsPage />} />
      <Route path="bots/:id/booking" element={<OnboardingBookingPage />} />
      <Route path="bots/:id/lead-ads" element={<OnboardingLeadAdsPage />} />
      <Route path="bots/:id/plan" element={<OnboardingPlanPage />} />
      <Route path="bots/:id/knowledge" element={<OnboardingKnowledgePage />} />
    </Route>
  

        {/* Authenticated app layout */}
        <Route
          path="/app"
          element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }
        >
          {/* Dashboard as default for /app */}
          <Route index element={<DashboardPage />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="dashboard/health" element={<DashboardBotHealthPage />} />
          <Route path="dashboard/conversion" element={<DashboardConversionPage />} />

          {/* Core client pages */}
          <Route path="bots" element={<BotsListPage />} />
          <Route path="bots/new" element={<BotCreatePage />} />
          <Route path="bots/:id" element={<BotDetailPage />} />
          <Route path="bots/:id/channels" element={<BotChannelsPage />} />
          <Route path="bots/:id/conversations" element={<BotConversationsPage />} />
          <Route path="bots/:id/knowledge" element={<BotKnowledgePage />} />
          <Route path="bots/:id/knowledge/jobs/:jobId" element={<BotKnowledgeJobPage />} />
          <Route path="bots/:id/features" element={<BotFeaturesPage />} />
          <Route path="bots/:id/plan" element={<BotPlanPage />} />

          {/* NEW: settings page */}
          <Route path="bots/:id/settings" element={<BotSettingsPage />} />

          <Route path="billing" element={<BillingPage />} />
          <Route path="account" element={<ProfilePage />} />

          <Route path="bots/:id/whatsapp-templates" element={<BotWhatsappTemplatesPage />}/>

          {/* Referrals portal */}
          <Route
            path="referrals"
            element={
              <RequireRole allowed={["REFERRER", "ADMIN"]}>
                <ReferrerPortalPage />
              </RequireRole>
            }
          />

          {/* Admin portal */}
          <Route
            path="admin/referrals"
            element={
              <RequireRole allowed={["ADMIN"]}>
                <AdminReferralsPage />
              </RequireRole>
            }
          />
          <Route
            path="admin/referrals/partners/:id"
            element={
              <RequireRole allowed={["ADMIN"]}>
                <AdminReferralPartnerDetailPage />
              </RequireRole>
            }
          />
          <Route
            path="admin/users"
            element={
              <RequireRole allowed={["ADMIN"]}>
                <AdminUsersPage />
              </RequireRole>
            }
          />
          <Route
            path="admin/bots"
            element={
              <RequireRole allowed={["ADMIN"]}>
                <AdminBotsPage />
              </RequireRole>
            }
          />
          <Route
            path="admin/bookings"
            element={
              <RequireRole allowed={["ADMIN"]}>
                <AdminBookingsPage />
              </RequireRole>
            }
          />
          <Route
            path="admin/emails"
            element={
              <RequireRole allowed={["ADMIN"]}>
                <AdminEmailUsagePage />
              </RequireRole>
            }
          />
          <Route
            path="admin/payments"
            element={
              <RequireRole allowed={["ADMIN"]}>
                <AdminPaymentsPage />
              </RequireRole>
            }
          />
          <Route
            path="admin/openai-usage"
            element={
              <RequireRole allowed={["ADMIN"]}>
                <AdminOpenAIUsagePage />
              </RequireRole>
            }
          />
          <Route
            path="admin/integrations"
            element={
              <RequireRole allowed={["ADMIN"]}>
                <AdminIntegrationsPage />
              </RequireRole>
            }
          />
          <Route
            path="admin/plans"
            element={
              <RequireRole allowed={["ADMIN"]}>
                <AdminPlansPage />
              </RequireRole>
            }
          />

          
        </Route>

        {/* Conversations top-level route for details */}
        <Route
          path="/app/conversations/:id"
          element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }
        >
          <Route index element={<ConversationDetailPage />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
};

const NotFound: React.FC = () => (
  <div className="page-container">
    <h2>Page not found</h2>
    <p>
      Check the URL or go back to the <a href="/">home page</a>.
    </p>
  </div>
);

export default App;
